import React, { useEffect, useMemo, useState } from 'react';
import BigNumber from 'bignumber.js';
import { ChainId, Token, Tokens, NETWORK_CONFIG } from '../../../../constants';
import { BridgeStrategy, BridgeConfig } from '../../types';
import BridgeToken from '../../BridgeToken';
import useTokenApprove from '@/hooks/use-token-approve';
import useTokenBalance from '@/hooks/use-token-balance';
import useMetisLayerzeroAdapter from '@/hooks/useMetisLayerzeroAdapter';
import AlertFunc from '@/utils/components/alert';
import ModalFunc from '@/utils/components/modal';
import user from '@/stores/user';
import { observer } from 'mobx-react';

interface MetisToGoatBridgeProps {
    fromChainId: ChainId;
    toChainId: ChainId;
    currentToken: Token;
    setCurrentToken: (token: Token) => void;
    onTransactionSuccess: (
        tx: string,
        amount: BigNumber,
        tokenName: string,
    ) => void;
}

function MetisToGoatBridge({
    fromChainId,
    toChainId,
    currentToken,
    setCurrentToken,
    onTransactionSuccess,
}: MetisToGoatBridgeProps) {
    const { connected, address: walletAddress } = user;

    const [inputAmount, setInputAmount] = useState(new BigNumber(0));
    const [transferring, setTransferring] = useState(false);
    const [transFeeInfo, setTransFeeInfo] = useState<{
        fee_num: number;
        servicesFee: number;
    }>({
        fee_num: 0,
        servicesFee: 0,
    });
    const [loadingTransFee, setLoadingTransFee] = useState(false);
    const [inputErrorMessage, setInputErrorMessage] = useState<
        string | undefined
    >(undefined);

    const { approved, approveUserTokenForSpender } = useTokenApprove(
        currentToken,
        inputAmount,
        fromChainId,
        toChainId,
    );

    const { estimateLayerzeroFee, sendToGoat } = useMetisLayerzeroAdapter();

    const {
        balance: currentTokenBalance,
        getBalance: updateCurrentTokenBalance,
    } = useTokenBalance(
        (Tokens[currentToken] as any)[fromChainId]?.address || '',
        undefined,
    );

    // Bridge configuration for Metis -> GOAT
    const bridgeConfig: BridgeConfig = useMemo(() => {
        return {
            etaText: `From ${NETWORK_CONFIG[fromChainId].name} to ${NETWORK_CONFIG[toChainId].name}: less than 10 minutes`,
            etaTooltip: `From ${NETWORK_CONFIG[fromChainId].name} to ${NETWORK_CONFIG[toChainId].name}: less than 10 minutes`,
            timeDisplay: 'Less than 10 minutes',
            showProtocolFee: false,
            showDate: false,
            gasTokenName: 'METIS',
        };
    }, [fromChainId, toChainId]);

    const { balance: gasTokenBalance } = useTokenBalance('');

    // Input validation for Metis -> GOAT
    useEffect(() => {
        if (inputAmount.isGreaterThan(currentTokenBalance)) {
            setInputErrorMessage('Insufficient Balance');
        } else if (inputAmount.isGreaterThan(0)) {
            // For Metis -> GOAT, check if enough METIS is reserved
            getLeftAmount().then((leftAmount) => {
                if (leftAmount) {
                    if (
                        (currentToken !== Token.metis &&
                            gasTokenBalance.isLessThan(leftAmount)) ||
                        (currentToken === Token.metis &&
                            gasTokenBalance
                                .minus(inputAmount)
                                .isLessThan(leftAmount))
                    ) {
                        setInputErrorMessage(
                            `You should reserve at least ${
                                leftAmount?.toFixed() || '-'
                            } METIS as the bridging cost to ${
                                NETWORK_CONFIG[toChainId].name
                            }.`,
                        );
                    } else {
                        setInputErrorMessage(undefined);
                    }
                }
            });
        } else {
            setInputErrorMessage(undefined);
        }
    }, [inputAmount, currentTokenBalance, gasTokenBalance, currentToken]);

    useEffect(() => {
        setInputAmount(new BigNumber(0));
    }, [fromChainId]);

    async function transfer(recipientAddress: string): Promise<string | false> {
        setTransferring(true);

        let tx: any = false;

        try {
            tx = await sendToGoat({
                token: currentToken,
                amount: inputAmount,
                recipientAddress: recipientAddress,
            });

            if (tx) {
                onTransactionSuccess(
                    tx,
                    inputAmount,
                    (Tokens[currentToken] as any)[fromChainId]?.symbol || '',
                );
                setInputAmount(new BigNumber(0));
            }
        } catch (error) {
            console.error('Metis to GOAT bridge transfer error:', error);
        }

        setTransferring(false);
        return tx;
    }

    async function getTransFeeDetail(
        recipientAddress: string,
        amount: BigNumber,
    ) {
        if (!approved || transferring) return;

        if (
            amount.isGreaterThan(0) &&
            currentTokenBalance.isGreaterThanOrEqualTo(amount)
        ) {
            try {
                setLoadingTransFee(true);

                const feeEstimate = await estimateLayerzeroFee({
                    token: currentToken,
                    amount: amount,
                    recipientAddress: recipientAddress,
                });

                if (feeEstimate) {
                    setTransFeeInfo({
                        fee_num: parseFloat(feeEstimate.feeInEther) || 0,
                        servicesFee: 0,
                    });
                } else {
                    setTransFeeInfo({
                        fee_num: 0,
                        servicesFee: 0,
                    });
                }
            } catch (e) {
                console.error('getTransFeeDetail error');
                console.error(e);
            }
            setLoadingTransFee(false);
        }
    }

    async function getLeftAmount(): Promise<BigNumber | undefined> {
        if (!currentTokenBalance || !approved) return undefined;

        try {
            const feeEstimate = await estimateLayerzeroFee({
                token: currentToken,
                amount: currentTokenBalance,
                recipientAddress: walletAddress || '',
            });

            if (feeEstimate) {
                return new BigNumber(feeEstimate.feeInEther);
            }
        } catch (e) {
            console.error('getLeftAmount LayerZero estimation error:', e);
        }

        return undefined;
    }

    async function handleMax(recipientAddress: string) {
        if (!currentTokenBalance) return;

        if (
            currentTokenBalance.isGreaterThan(0) &&
            currentToken === Token.metis
        ) {
            try {
                const feeEstimate = await estimateLayerzeroFee({
                    token: currentToken,
                    amount: currentTokenBalance,
                    recipientAddress: recipientAddress,
                });

                if (feeEstimate) {
                    const layerzeroFee = new BigNumber(feeEstimate.feeInEther);
                    const leftAmount = currentTokenBalance
                        .minus(layerzeroFee)
                        .minus(0.01);

                    if (leftAmount.isLessThanOrEqualTo(0)) {
                        ModalFunc({
                            type: 'sorry',
                            title: 'Sorry',
                            text: `The current ${
                                Tokens[currentToken][fromChainId]?.symbol || ''
                            } Token balance ${currentTokenBalance.toFixed(
                                3,
                            )} on your account is not sufficient to support this execution. Please leave at least ${layerzeroFee.toFixed(
                                6,
                            )} ${
                                Tokens[currentToken][fromChainId]?.symbol || ''
                            } to support this execution`,
                            buttonText: 'OK',
                        });
                    } else {
                        setInputAmount(leftAmount);

                        AlertFunc({
                            type: 'info',
                            text: `Your estimated gas fee has been subtracted from your balance.`,
                            duration: 10000,
                        });
                    }
                }
            } catch (e) {
                console.error(e);
            }
        } else {
            setInputAmount(currentTokenBalance);
        }
    }

    const bridgeStrategy: BridgeStrategy = {
        currentToken,
        setCurrentToken,
        currentTokenBalance,
        updateTokenBalance: updateCurrentTokenBalance,
        inputAmount,
        setInputAmount,
        inputErrorMessage,
        approved,
        approveToken: approveUserTokenForSpender,
        transfer,
        transferring,
        estimateFee: getTransFeeDetail,
        transFeeInfo,
        loadingTransFee,
        handleMax,
        getLeftAmount,
        fromChainId,
        toChainId,
        bridgeConfig,
    };

    return (
        <BridgeToken
            strategy={bridgeStrategy}
            connected={connected}
            walletAddress={walletAddress}
            onConnect={() => user.toggleConnectModal()}
        />
    );
}

export default observer(MetisToGoatBridge);
